import { HStack, Icon, Text, VStack } from "@biblioteksentralen/react";
import { AccessibilityInfo } from "@libry-content/types";
import { LibraryInfoSection } from "../contactInfo/ContactInfo";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { Check, X } from "react-feather";
import { sort } from "radash";

interface Props {
  accessibilityInfo?: AccessibilityInfo;
}

type AccessibilityInfoPart = {
  value: string | undefined;
  conditionSatisfied: boolean;
  getLabel: (value: string) => string;
  extraDescription?: string;
};

const AccessibilityInfoView = ({ accessibilityInfo }: Props) => {
  const { t, ts } = useTranslation();
  if (!accessibilityInfo) return null;

  const { entrance, toilets, floors, seatingAreas, inaccessibleFloorsDescription, accessibleParkingSpaces } =
    accessibilityInfo;

  const accessibilityInfoParts: AccessibilityInfoPart[] = [
    {
      value: entrance,
      conditionSatisfied: entrance === "wheelchairAccessible",
      getLabel: (entrance) =>
        entrance === "wheelchairAccessible"
          ? t("Inngang for rullestolbrukere")
          : t("Ingen inngang for rullestolbrukere"),
    },
    {
      value: toilets,
      conditionSatisfied: toilets === "wheelchairAccessible",
      getLabel: (toilets) => {
        if (toilets === "wheelchairAccessible") return t("Toalett for rullestolbrukere");
        if (toilets === "notWheelchairAccessible") return t("Ingen toalett for rullestolbrukere");
        else return t("Ingen toalett tilgjengelig i biblioteket");
      },
    },
    {
      value: accessibleParkingSpaces,
      conditionSatisfied: accessibleParkingSpaces === "available",
      getLabel: (accessibleParkingSpaces) =>
        accessibleParkingSpaces === "available"
          ? t("Parkering for forflytningshemmede")
          : t("Ingen parkering for forflytningshemmede"),
    },
    {
      value: seatingAreas,
      conditionSatisfied: seatingAreas === "wheelchairAccessible",
      getLabel: (seatingAreas) =>
        seatingAreas === "wheelchairAccessible"
          ? t("Sitteplass med bord tilpasset rullestolbrukere")
          : t("Ingen sitteplass med bord tilpasset rullestolbrukere"),
    },
    {
      value: floors,
      conditionSatisfied: floors === "wheelchairAccessible" || floors === "singleFloor",
      getLabel: (floors) => {
        if (floors === "wheelchairAccessible") return t("Tilgang til alle etasjer for rullestolbrukere");
        if (floors === "singleFloor") return t("Hele biblioteket er på ett plan");
        else return t("Deler av biblioteket er utilgjengelig for rullestolbrukere");
      },
      extraDescription: ts(inaccessibleFloorsDescription),
    },
  ];

  const accessibilityInfoPartsSorted = sort(accessibilityInfoParts, (part) => (part.conditionSatisfied ? -1 : 1));

  return (
    <LibraryInfoSection label={t("Tilgjengelighet og tilrettelegging")} spacing="0.75rem">
      {accessibilityInfoPartsSorted.map(
        (accessibilityInfoPart, index) =>
          accessibilityInfoPart.value && (
            // key = index fordi her skal ikke rekkefølge endres
            <VStack key={index} marginLeft="0.15rem" gap="0.125rem" alignItems="flex-start">
              <HStack spacing="0.35rem" display="flex" alignItems="flex-start">
                <Icon
                  as={accessibilityInfoPart.conditionSatisfied ? Check : X}
                  marginTop="0.125rem"
                  width="1.25rem"
                  height="1.25rem"
                  fontSize="1.25rem"
                />
                <Text width="100%">{accessibilityInfoPart.getLabel(accessibilityInfoPart.value)}</Text>
              </HStack>
              {accessibilityInfoPart.extraDescription && !accessibilityInfoPart.conditionSatisfied && (
                <Text marginLeft="1.75rem" fontSize="sm" fontStyle="italic">
                  {accessibilityInfoPart.extraDescription}
                </Text>
              )}
            </VStack>
          )
      )}
    </LibraryInfoSection>
  );
};

export default AccessibilityInfoView;
